<script lang="ts">
  // GameView2v2 — layout inspirado no design de referencia do user
  // (mockup spintruco). 3 rows verticais:
  //   1. Partner: avatar + hand HORIZONTAIS no topo
  //   2. Middle: opp-left | plays-cross | opp-right
  //      (cross = partner_play, opp_l_play, vira, opp_r_play, me_play)
  //   3. Me: hand clicavel + avatar HORIZONTAIS embaixo
  //
  // Action buttons (TrucoBtn / Abandon) ficam em corners separados.

  import type { MatchSnapshot, Card as CardType } from '../../stores/match';
  import { displayedMatchSnapshot, clearHold } from '../../stores/displayed';
  import { userId, avatar as myAvatarStore } from '../../stores/auth';
  import { findMe2v2 } from '../../lib/teams';
  import { resolveAvatar } from '../../lib/avatars';
  import Hand from './Hand.svelte';
  import Card from './Card.svelte';
  import Vira from './Vira.svelte';
  import OppHand from './OppHand.svelte';
  import PlayerPortrait from './PlayerPortrait.svelte';
  import Scoreboard2v2 from './Scoreboard2v2.svelte';
  import ActionPrompt from './ActionPrompt.svelte';
  import TrucoButton from './TrucoButton.svelte';
  import TrucoActions from './TrucoActions.svelte';
  import ManoEspecialBanner from './ManoEspecialBanner.svelte';
  import FinishedModal from './FinishedModal.svelte';
  import CinematicCollect from './CinematicCollect.svelte';
  import { receiveCard } from '../../lib/cardTransition';
  // Cartas de opp/parceiro: transition Svelte mount-safe em vez de
  // classe CSS .card-fly-from-* — keyframe suprimida em mobile/background
  // deixava a carta presa em opacity:0 ("oponente jogou e nao apareceu",
  // Leonardo 2026-06-11). Mesmo fix do 1v1 (Plays.svelte, 2026-05-27).
  import { flyFromTop, flyFromLeft, flyFromRight } from '../../lib/flyTransitions';
  import { ANIM } from '../../lib/animConfig';
  import { api } from '../../lib/api';

  // DEV mode: botoes de set-score so aparecem em localhost. Em prod o
  // backend nao expoe /dev/* mesmo, mas evitamos o flicker do botao.
  const isDev =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1');

  async function devSetScore(teamA: number, teamB: number) {
    try {
      await api.post('/dev/set-score', { team_a: teamA, team_b: teamB });
    } catch (e) {
      console.warn('dev set-score:', e);
    }
  }

  // cardKey: bate com a chave usada em Hand.svelte (sendCard) PRA MIM —
  // a minha carta tem crossfade pair que faz match exato e desliza
  // do slot da mao. Pros oppents/parceiro nao ha origem rastreavel
  // (a mao deles e generic card-backs), entao a carta deles usa
  // flyAndSpinFrom — vem girando da direcao do player ate o slot
  // do cross.
  function cardKey(card: CardType | null | undefined, slot: string): string {
    if (card?.value && card?.suit) return `${card.value}${card.suit}`;
    return `back-${slot}`;
  }

  type Props = { snap: MatchSnapshot };
  let { snap }: Props = $props();

  // displaySnap segura cartas da mesa por uns segundos quando vaza
  // fecha (UX: user precisa ver a carta antes do snap "limpar"). Os
  // modais (Truco/Finished) continuam recebendo `snap` LIVE pra
  // resposta imediata — so o visual da mesa usa o held.
  let displaySnap = $derived($displayedMatchSnapshot ?? snap);

  let seats = $derived(findMe2v2(displaySnap, $userId));
  let me = $derived(seats.me);
  let partner = $derived(seats.partner);
  let oppLeft = $derived(seats.oppLeft);
  let oppRight = $derived(seats.oppRight);

  let isMyTurn = $derived(
    displaySnap.status === 'running' &&
    !displaySnap.truco_challenge_running &&
    displaySnap.next_to_play_id === $userId,
  );

  let pendingFromMyTeam = $derived(
    displaySnap.truco_challenge_running &&
    !!displaySnap.truco_challenger_id &&
    seats.mineIds.includes(displaySnap.truco_challenger_id),
  );
  let pendingAgainstMyTeam = $derived(
    displaySnap.truco_challenge_running &&
    !!displaySnap.truco_challenger_id &&
    !seats.mineIds.includes(displaySnap.truco_challenger_id),
  );

  type PromptMode = 'mine' | 'theirs' | 'truco-me' | 'truco-opp' | 'finished-win' | 'finished-lose' | 'idle';
  let promptMode = $derived.by((): PromptMode => {
    if (displaySnap.status === 'finished') {
      const won = !!displaySnap.winner && seats.mineIds.includes(displaySnap.winner);
      return won ? 'finished-win' : 'finished-lose';
    }
    if (pendingFromMyTeam) return 'truco-me';
    if (pendingAgainstMyTeam) return 'truco-opp';
    if (isMyTurn) return 'mine';
    return 'theirs';
  });
  let promptText = $derived.by(() => {
    switch (promptMode) {
      case 'mine': return 'sua vez';
      case 'theirs': return 'aguardando';
      case 'truco-me': return 'aguardando resposta';
      case 'truco-opp': return 'TRUCO! responda';
      case 'finished-win': return 'vitória!';
      case 'finished-lose': return 'fim de partida';
      default: return '—';
    }
  });

  function playsOf(p: typeof me): CardType[] {
    if (!p) return [];
    return [p.first_play, p.second_play, p.third_play].filter(
      (c): c is CardType => !!c?.value,
    );
  }
  // Em 2v2 mostramos so a CARTA MAIS RECENTE de cada player na cross
  // (ultima da lista) — design do mockup tem 1 carta por posicao no cross.
  function latestPlay(p: typeof me): CardType | undefined {
    const all = playsOf(p);
    return all[all.length - 1];
  }
  let mePlay = $derived(latestPlay(me));
  let partnerPlay = $derived(latestPlay(partner));

  let leftPlay = $derived(latestPlay(oppLeft));
  let rightPlay = $derived(latestPlay(oppRight));

  // ═══════════════════════════════════════════════════════════════════
  // Cinematografia D (Felipe 2026-05-20, v2 — abordagem isolada e simples)
  // ═══════════════════════════════════════════════════════════════════
  //
  // Princípios:
  //   1. SÓ mexer em GameView2v2 + CinematicCollect.svelte
  //   2. NÃO tocar em Hand, displayed.ts, latestPlay, displayedMatchSnapshot
  //   3. Backend pausa bot/turn-timer durante a anim via TrickCloseWarmup
  //      e DealWarmup — snap LIVE estável durante toda anim
  //   4. Detectar via displaySnap (não snap LIVE) — snap LIVE muda quando
  //      4ª carta entra mas displaySnap só atualiza depois (hold do
  //      displayedMatchSnapshot). Os dois mudam em momentos diferentes.
  //      Reagir a displaySnap = reagir quando o front realmente vai
  //      RENDERIZAR a vaza fechada na mesa
  //   5. Feature flag ANIM.ENABLED desliga tudo
  type Pos = { x: number; y: number };
  let crossEl: HTMLDivElement | null = $state(null);
  let topSlotEl: HTMLDivElement | null = $state(null);
  let leftSlotEl: HTMLDivElement | null = $state(null);
  let bottomSlotEl: HTMLDivElement | null = $state(null);
  let rightSlotEl: HTMLDivElement | null = $state(null);
  let viraSlotEl: HTMLDivElement | null = $state(null);

  let cinematicActive = $state(false);
  let cinematicSnapshot = $state<{
    topCard: CardType | null;
    leftCard: CardType | null;
    bottomCard: CardType | null;
    rightCard: CardType | null;
    viraCard: CardType | null;
    durationMs: number;
    topPos: Pos;
    leftPos: Pos;
    bottomPos: Pos;
    rightPos: Pos;
    centerPos: Pos;
  } | null>(null);

  function centerOf(el: HTMLElement, containerRect: DOMRect): Pos {
    const r = el.getBoundingClientRect();
    return {
      x: r.left + r.width / 2 - containerRect.left,
      y: r.top + r.height / 2 - containerRect.top,
    };
  }

  // Dispara cinematic quando displaySnap atinge novo estado de "vaza
  // fechada" ou "mao fechada". displaySnap está em hold pelo subscribe
  // do displayedMatchSnapshot — então quando ele finalmente muda, a UI
  // está MOSTRANDO a vaza fechada com 4 cartas (estado ideal pra capturar).
  let lastSeenDispRound = $state(displaySnap.round_number ?? 0);
  let lastSeenDispTC = $state(displaySnap.tricks_closed ?? 0);
  let pendingCinematicTimer: number | null = null;
  $effect(() => {
    if (!ANIM.ENABLED) return;
    const dispRN = displaySnap.round_number ?? 0;
    const dispTC = displaySnap.tricks_closed ?? 0;
    const handClosed = dispRN > lastSeenDispRound && lastSeenDispRound > 0;
    const trickClosed = dispTC > lastSeenDispTC && !handClosed;
    // Cinematic SO em fim de vaza (meio da mao). Em fim de mao, a Vira
    // component ja tem seu reveal anim proprio (vira-reveal keyframe) e
    // sobrepor o overlay estava causando teleporte visual da vira nova.
    // Felipe 2026-05-20: percepcao do player e fragil — mais seguro
    // desligar do que entregar com bug visivel.
    if (trickClosed && crossEl && !cinematicActive && pendingCinematicTimer === null) {
      // Captura cartas + posições AGORA (snap atual mostra 4 cartas);
      // delay pra dar tempo do user ver a 4a carta parada antes
      // do recolhimento. Backend pausa bot/timer por VIEW_DELAY + COLLECT.
      const containerRect = crossEl.getBoundingClientRect();
      const pendingSnapshot = {
        topCard: partnerPlay ?? null,
        leftCard: leftPlay ?? null,
        bottomCard: mePlay ?? null,
        rightCard: rightPlay ?? null,
        // Vira NUNCA entra no overlay cinematic:
        //  - trick close: a mesma vira continua valendo (sem recolher)
        //  - hand close: a vira NOVA ja tem seu proprio reveal anim
        //    (Vira.svelte usa {#key} + .revealing). Incluir aqui dava
        //    flip duplo — sobreposicao visual confusa.
        viraCard: null,
        durationMs: handClosed ? ANIM.HAND_COLLECT_MS : ANIM.TRICK_COLLECT_MS,
        topPos: topSlotEl ? centerOf(topSlotEl, containerRect) : { x: 0, y: 0 },
        leftPos: leftSlotEl ? centerOf(leftSlotEl, containerRect) : { x: 0, y: 0 },
        bottomPos: bottomSlotEl ? centerOf(bottomSlotEl, containerRect) : { x: 0, y: 0 },
        rightPos: rightSlotEl ? centerOf(rightSlotEl, containerRect) : { x: 0, y: 0 },
        centerPos: viraSlotEl ? centerOf(viraSlotEl, containerRect) : { x: 0, y: 0 },
      };
      const delay = handClosed ? ANIM.HAND_VIEW_DELAY_MS : ANIM.TRICK_VIEW_DELAY_MS;
      pendingCinematicTimer = window.setTimeout(() => {
        cinematicSnapshot = pendingSnapshot;
        cinematicActive = true;
        pendingCinematicTimer = null;
      }, delay);
    }
    lastSeenDispRound = dispRN;
    lastSeenDispTC = dispTC;
  });

  // Esconder slots originais APOS cinematic: cosmetico via CSS, NAO toca
  // em latestPlay. Reset acontece em duas condicoes:
  //   1. Uma carta nova foi jogada (playsSignature mudou) — desliga hide
  //   2. Fallback timeout 8s — se nada acontecer, slots reaparecem (no
  //      pior caso o user ve cartas antigas brevemente, NUNCA cartas
  //      sumindo do nada — preserva o invariante do Felipe).
  let slotsHidden = $state(false);
  let signatureAtHide = $state<string | null>(null);
  let hideFallbackTimer: number | null = null;

  function playsSignature(): string {
    const players = [me, partner, oppLeft, oppRight];
    return players
      .map((p) => {
        if (!p) return '_';
        return `${p.first_play?.value ?? ''}-${p.second_play?.value ?? ''}-${p.third_play?.value ?? ''}`;
      })
      .join('|');
  }

  function onCinematicDone() {
    clearHold();
    cinematicActive = false;
    cinematicSnapshot = null;
    // Captura signature ATUAL e esconde slots ate signature mudar
    // (alguem jogou) ou fallback expirar.
    signatureAtHide = playsSignature();
    slotsHidden = true;
    if (hideFallbackTimer !== null) clearTimeout(hideFallbackTimer);
    hideFallbackTimer = window.setTimeout(() => {
      slotsHidden = false;
      signatureAtHide = null;
      hideFallbackTimer = null;
    }, 8000);
  }

  // Carta nova chegou (signature mudou apos hide) → libera os slots.
  $effect(() => {
    if (!slotsHidden) return;
    if (playsSignature() !== signatureAtHide) {
      slotsHidden = false;
      signatureAtHide = null;
      if (hideFallbackTimer !== null) {
        clearTimeout(hideFallbackTimer);
        hideFallbackTimer = null;
      }
    }
  });

  function activeFor(playerId: string | undefined): boolean {
    if (!playerId) return false;
    return displaySnap.next_to_play_id === playerId && !displaySnap.truco_challenge_running;
  }
</script>

<section class="game-2v2">
  <div class="head">
    <Scoreboard2v2 snap={displaySnap} vira={displaySnap.manilha} />
  </div>

  <div class="mesa">
    <!-- Row 1: Partner — avatar + hand horizontais -->
    <div class="partner-row">
      <PlayerPortrait
        name={partner?.name ?? '—'}
        side="opp"
        active={activeFor(partner?.id)}
        avatarKey={partner ? resolveAvatar(partner.avatar, partner.id, $myAvatarStore, true) : undefined}
        deadline={activeFor(partner?.id) ? displaySnap.deadline : null}
        turnDurationSec={displaySnap.turn_duration_sec ?? 30}
        playerId={partner?.id}
        ranking={partner?.ranking_score}
      />
      <OppHand
        cards={partner?.cards ?? []}
        roundNumber={displaySnap.round_number ?? 0}
        revealCards={displaySnap.mano_especial_active && !!displaySnap.mano_especial_player_id && seats.mineIds.includes(displaySnap.mano_especial_player_id)}
      />
    </div>

    <!-- Row 2: opp-left | plays-cross | opp-right -->
    <div class="middle-row">
      <div class="side-seat left">
        <PlayerPortrait
          name={oppLeft?.name ?? '—'}
          side="opp"
          active={activeFor(oppLeft?.id)}
          avatarKey={oppLeft ? resolveAvatar(oppLeft.avatar, oppLeft.id, $myAvatarStore, true) : undefined}
          deadline={activeFor(oppLeft?.id) ? displaySnap.deadline : null}
          turnDurationSec={displaySnap.turn_duration_sec ?? 30}
          playerId={oppLeft?.id}
          ranking={oppLeft?.ranking_score}
        />
        <OppHand cards={oppLeft?.cards ?? []} roundNumber={displaySnap.round_number ?? 0} />
      </div>

      <div
        class="plays-cross"
        class:cinematic-active={cinematicActive}
        class:slots-hidden={slotsHidden}
        bind:this={crossEl}
      >
        <div class="cross-slot top" bind:this={topSlotEl}>
          {#if partnerPlay}
            <div class="play-anim" in:flyFromTop>
              <Card card={partnerPlay} vira={displaySnap.manilha} size="md" />
            </div>
          {/if}
        </div>
        <div class="cross-mid">
          <div class="cross-slot left" bind:this={leftSlotEl}>
            {#if leftPlay}
              <div class="play-anim" in:flyFromLeft>
                <Card card={leftPlay} vira={displaySnap.manilha} size="md" />
              </div>
            {/if}
          </div>
          <div bind:this={viraSlotEl}>
            <Vira vira={displaySnap.manilha} />
          </div>
          <div class="cross-slot right" bind:this={rightSlotEl}>
            {#if rightPlay}
              <div class="play-anim" in:flyFromRight>
                <Card card={rightPlay} vira={displaySnap.manilha} size="md" />
              </div>
            {/if}
          </div>
        </div>
        <div class="cross-slot bottom" bind:this={bottomSlotEl}>
          {#if mePlay}
            <div
              class="play-anim"
              in:receiveCard={{ key: cardKey(mePlay, 'bottom') }}
            >
              <Card card={mePlay} vira={displaySnap.manilha} size="md" />
            </div>
          {/if}
        </div>
        {#if cinematicActive && cinematicSnapshot}
          <CinematicCollect
            topCard={cinematicSnapshot.topCard}
            leftCard={cinematicSnapshot.leftCard}
            bottomCard={cinematicSnapshot.bottomCard}
            rightCard={cinematicSnapshot.rightCard}
            viraCard={cinematicSnapshot.viraCard}
            durationMs={cinematicSnapshot.durationMs}
            topPos={cinematicSnapshot.topPos}
            leftPos={cinematicSnapshot.leftPos}
            bottomPos={cinematicSnapshot.bottomPos}
            rightPos={cinematicSnapshot.rightPos}
            centerPos={cinematicSnapshot.centerPos}
            onDone={onCinematicDone}
          />
        {/if}
      </div>

      <div class="side-seat right">
        <PlayerPortrait
          name={oppRight?.name ?? '—'}
          side="opp"
          active={activeFor(oppRight?.id)}
          avatarKey={oppRight ? resolveAvatar(oppRight.avatar, oppRight.id, $myAvatarStore, true) : undefined}
          deadline={activeFor(oppRight?.id) ? displaySnap.deadline : null}
          turnDurationSec={displaySnap.turn_duration_sec ?? 30}
          playerId={oppRight?.id}
          ranking={oppRight?.ranking_score}
        />
        <OppHand cards={oppRight?.cards ?? []} roundNumber={displaySnap.round_number ?? 0} />
      </div>
    </div>

    <!-- Row 3: Me — hand clicavel + avatar horizontais -->
    <div class="me-row">
      <div class="my-hand-wrap">
        <Hand cards={me?.cards ?? []} vira={displaySnap.manilha} canPlay={isMyTurn} snap={snap} />
      </div>
      <PlayerPortrait
        name={me?.name ?? '—'}
        side="me"
        active={isMyTurn}
        avatarKey={me ? resolveAvatar(me.avatar, me.id, $myAvatarStore, false) : undefined}
        deadline={isMyTurn ? displaySnap.deadline : null}
        turnDurationSec={displaySnap.turn_duration_sec ?? 30}
        playerId={me?.id}
        ranking={me?.ranking_score}
      />
    </div>
  </div>

  <!-- DEV: shortcuts pra testar mao de 11 sem jogar 11 maos. SO em
       localhost — em prod (Surge) os botoes nem renderizam. -->
  {#if isDev}
    <div class="dev-bar">
      <button type="button" onclick={() => devSetScore(10, 5)}>DEV 10/5</button>
      <button type="button" onclick={() => devSetScore(11, 5)}>DEV 11/5</button>
      <button type="button" onclick={() => devSetScore(5, 11)}>DEV 5/11</button>
      <button type="button" onclick={() => devSetScore(11, 11)}>DEV ferro 11/11</button>
    </div>
  {/if}

  <!-- Action bar: prompt + truco-btn na esquerda/direita. Quando
       ha truco pendente contra meu time, TrucoActions toma toda a
       linha. Em mao de 11 (truco locked, time no threshold), o
       ManoEspecialBanner substitui — ambos times veem alerta. -->
  {#if snap.mano_especial_pending}
    <div class="action-bar">
      <ManoEspecialBanner snap={displaySnap} />
    </div>
  {:else if snap.truco_challenge_running && snap.truco_challenger_id && !seats.mineIds.includes(snap.truco_challenger_id)}
    <div class="action-bar">
      <TrucoActions {snap} />
    </div>
  {:else}
    <div class="action-bar">
      <ActionPrompt
        mode={promptMode}
        text={promptText}
        deadline={pendingFromMyTeam ? snap.deadline : null}
      />
      <TrucoButton {snap} />
    </div>
  {/if}
</section>

<!-- Modal usa LIVE snap — quer resposta imediata, sem hold. -->
<FinishedModal {snap} />

<style>
  .game-2v2 {
    width: 100%;
    /* Felipe 2026-05-21: desktop esticado / cartas sobrepostas — fix:
     *   1. max-width 480px (shell mobile)
     *   2. container-type pra cartas usarem cqw (largura do container)
     *      em vez de vw (largura da window). Sem isso, cartas md
     *      eram 66px em viewport 1200px, grande demais pro shell 480. */
    max-width: 480px;
    height: 100%;
    min-height: 0;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: clamp(4px, 1vh, 8px);
    padding: 0 6px;
    /* container-type:size mede largura E altura — cards usam max(cqw,
     * cqh) pra crescer no eixo mais folgado. Em desktop com altura
     * sobrando, cqh domina e cards ficam maiores. Em mobile apertado,
     * o eixo limitante mantem cards pequenos. Felipe 2026-05-21: "mais
     * altura maior, senao fica apertado". */
    container-type: size;
    container-name: game-shell;
  }
  .game-2v2 {
    /* Factor cqh menor pra iPhone SE (375x667) nao inflar — cqw domina
     * em mobile, cqh ajuda crescer so quando ha REAL sobra vertical
     * (desktop). Felipe 2026-05-21: "iPhone SE ta mt grande". */
    --card-w-sm: clamp(28px, max(7cqw, 4cqh), 46px);
    --card-w-md: clamp(34px, max(9cqw, 5.5cqh), 58px);
    --card-w-lg: clamp(40px, max(11cqw, 6.5cqh), 70px);
  }
  .head { display: flex; justify-content: center; flex: 0 0 auto; }

  /* Mesa fica com flex:1 — distribui filhos verticalmente.
   * partner-row e me-row ficam fixos (auto), middle-row absorve. */
  .mesa {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: clamp(4px, 1vh, 10px);
    padding: clamp(10px, 2vh, 18px);
    /* Mesa verde-feltro com efeito 3D — inclinada pra tras pra dar
     * sensacao de mesa real vista de cima/frente. Felipe 2026-05-20. */
    background:
      radial-gradient(ellipse at center, #25744a 0%, #103820 60%, #051c0d 100%);
    border: 2px solid #4a2c10;
    border-radius: clamp(20px, 4vw, 40px) / clamp(16px, 3vw, 28px);
    /* Inclina mesa pra tras (-8deg) com perspective leve. Conteudo
     * dentro nao distorce muito porque transform-style: flat. */
    perspective: 1200px;
    transform: rotateX(6deg);
    transform-origin: center top;
    /* Sombras 3D:
     *  - inset escura nas bordas = vinheta de mesa
     *  - inset clara no topo = highlight de feltro elevado
     *  - outer drop shadow = mesa elevada do "chao"
     *  - inner glow leve = brilho central */
    box-shadow:
      inset 0 0 80px rgba(0, 0, 0, 0.55),
      inset 0 2px 4px rgba(255, 255, 255, 0.08),
      inset 0 -20px 40px rgba(0, 0, 0, 0.35),
      0 24px 40px -12px rgba(0, 0, 0, 0.7),
      0 10px 20px rgba(0, 0, 0, 0.5);
  }

  /* Partner — avatar + hand horizontais */
  .partner-row {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: clamp(8px, 2vw, 14px);
    flex: 0 0 auto;
  }

  /* Middle: 3 colunas (opp-left | cross | opp-right) */
  .middle-row {
    flex: 1 1 auto;
    min-height: 0;
    display: grid;
    grid-template-columns: auto 1fr auto;
    align-items: center;
    gap: clamp(6px, 1.5vw, 12px);
  }
  .side-seat {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
  }
  /* Hand do oponente LATERAL vai em coluna (cartas empilhadas vertical
   * embaixo do portrait) — mimic mesa real onde left/right tem cartas
   * orientadas pro lado. :global() pq OppHand tem CSS scoped. */
  .side-seat :global(.opp-hand) {
    flex-direction: column;
    gap: 3px;
    /* Coluna: 1 carta de largura, 3 cartas + 2 gaps de altura. Override
     * dos min-width/height do .opp-hand horizontal pra side-seat nao
     * subir/descer quando cartas saem (Felipe 2026-05-20). */
    min-width: calc(var(--card-w-md) + 12px);
    min-height: calc(var(--card-w-md) * 1.5 * 3 + 18px);
  }

  /* Plays cross — partner-play em cima, opp_l/vira/opp_r no meio,
   * me-play embaixo. Slots vazios mantem espaco pra cross nao
   * colapsar quando ninguem jogou. */
  .plays-cross {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: clamp(4px, 1vh, 8px);
    width: 100%;
    height: 100%;
  }
  .cross-slot {
    min-height: calc(var(--card-w-md) * 1.5);
    min-width: var(--card-w-md);
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .cross-mid {
    display: flex;
    align-items: center;
    gap: clamp(8px, 2vw, 14px);
  }

  /* Pos-cinematic: esconde apenas os slots de play (manter a vira visivel).
   * visibility: hidden mantem o layout intacto — quando snap evolui e
   * slotsHidden volta a false, slots reaparecem in place sem reflow. */
  .plays-cross.slots-hidden .cross-slot > .play-anim {
    visibility: hidden;
  }

  /* Me — hand + avatar horizontais */
  .me-row {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: clamp(8px, 2vw, 14px);
    flex: 0 0 auto;
  }
  .my-hand-wrap { flex: 0 0 auto; }

  /* DEV bar — shortcuts pra setar score */
  .dev-bar {
    display: flex;
    gap: 4px;
    flex-wrap: wrap;
    padding: 4px 0;
    font-size: 9px;
  }
  .dev-bar button {
    padding: 2px 6px;
    border: 1px dashed rgba(255, 210, 74, 0.4);
    background: rgba(255, 210, 74, 0.08);
    color: rgba(255, 210, 74, 0.8);
    border-radius: 4px;
    font-size: 9px;
    letter-spacing: 1px;
    cursor: pointer;
  }
  .dev-bar button:hover {
    background: rgba(255, 210, 74, 0.2);
  }

  /* Action bar abaixo da mesa — prompt esquerda, truco direita.
     Grid 1fr/auto pra timer do prompt (ex: "AGUARDANDO RESPOSTA 13s")
     mudar largura a cada segundo sem empurrar o botão TRUCO! lateralmente. */
  .action-bar {
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: center;
    gap: 10px;
    flex: 0 0 auto;
    padding: 0 4px;
  }

  @media (max-width: 520px) {
    .game-2v2 { padding: 0 4px; gap: clamp(3px, 0.8vh, 6px); }
    .mesa { padding: 6px; gap: clamp(3px, 0.8vh, 6px); }
    .partner-row, .me-row { gap: 8px; }
    .middle-row { gap: 6px; }
    .cross-mid { gap: 6px; }
  }
</style>
